// src/components/watchlist/SubscriptionDetailsDialog.js
import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import LocationMap from './LocationMap'

const SubscriptionDetailsDialog = ({ subscription, onClose }) => {
  const { t } = useTranslation()
  const [mapCenter, setMapCenter] = useState(null)

  // Calculate initial map center based on subscription type
  const getInitialCenter = () => {
    if (
      subscription.osm_data?.type === 'polygon' &&
      subscription.osm_data?.properties?.coordinates
    ) {
      // Use default center until polygon center is calculated
      return [52.3676, 4.9041]
    } else if (subscription.osm_data?.properties?.lat && subscription.osm_data?.properties?.lon) {
      return [subscription.osm_data.properties.lat, subscription.osm_data.properties.lon]
    }
    return [52.3676, 4.9041] // Default to Amsterdam if no location data
  }

  // Handle when map calculates the center of a polygon
  const handleSetMapCenter = (center) => {
    setMapCenter(center)
  }

  return (
    <Dialog open={Boolean(subscription)} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography variant="h6" component="div">
          {t('notifications.propertiesIn', 'Properties in')} {subscription.address}
        </Typography>
        <IconButton edge="end" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('notifications.alertDetails', 'Alert Details')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.type', 'Type')}:
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      subscription.type === 'rent'
                        ? t('notifications.toRent')
                        : t('notifications.toBuy')
                    }
                    color={subscription.type === 'rent' ? 'primary' : 'secondary'}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.priceRange', 'Price Range')}:
                  </Typography>
                  <Typography variant="body2">
                    €{subscription.min_price?.toLocaleString() || 0} - €
                    {subscription.max_price?.toLocaleString() || 0}
                    {subscription.type === 'rent' ? '/month' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.location', 'Location')}:
                  </Typography>
                  <Typography variant="body2">{subscription.address}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.status', 'Status')}:
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      subscription.enabled
                        ? t('notifications.active', 'Active')
                        : t('notifications.disabled', 'Disabled')
                    }
                    color={subscription.enabled ? 'success' : 'default'}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 200,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {t('notifications.locationArea', 'Location Area')}
                </Typography>

                {/* Map Preview */}
                <Box
                  sx={{
                    height: 170,
                    position: 'relative',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  <LocationMap
                    readOnly
                    initialLocation={subscription.osm_data}
                    center={mapCenter || getInitialCenter()}
                    zoom={12}
                    onCenterCalculated={handleSetMapCenter}
                  />
                </Box>

                <Typography variant="caption" display="block" color="text.secondary">
                  {subscription.osm_data?.type === 'polygon'
                    ? t('notifications.customArea', 'Custom area selected on map')
                    : t('notifications.specificLocation', 'Specific location selected')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Properties List */}
        <Box>
          <Typography variant="h6" gutterBottom>
            {subscription.listings?.[0]?.count || 0}{' '}
            {t('notifications.propertiesFound', 'Properties Found')}
          </Typography>

          {!subscription.listings?.[0]?.count || subscription.listings[0].count === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t(
                  'notifications.noPropertiesYet',
                  "No properties match your criteria yet. We'll notify you when new listings become available."
                )}
              </Typography>
            </Paper>
          ) : (
            <Typography variant="body2" paragraph>
              {t(
                'notifications.goToDiscover',
                'Go to the Discover section to view all matching properties.'
              )}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('notifications.close', 'Close')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            // Navigate to discover page with filter by this subscription
            window.location.href = `/discover/${subscription.id}`
            onClose()
          }}
        >
          {t('notifications.viewProperties', 'View Properties')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionDetailsDialog
